import { db, LEGACY_TENANT_ID } from "@/lib/firebase";
import { collection, addDoc, getDocs, query, orderBy, where, doc, getDoc, documentId, Timestamp, updateDoc, deleteDoc } from "firebase/firestore";
import { generateMockLeads } from "@/lib/mock-data";
import { logger } from "@/lib/logger";

export interface Challenge {
  id: string;
  title: string;
  description: string;
  startDate: Date | string;
  endDate: Date | string;
  metricType: "leads_closed" | "leads_contacted" | "new_leads";
  createdBy: string;
  createdAt: Date | string;
  isActive: boolean;
  participants?: string[]; // Array of user IDs who are participating in the challenge
  tenantId?: string;
}

export interface LeaderboardEntry {
  userId: string;
  userName: string;
  userAvatar?: string;
  score: number;
  rank?: number;
}

// Define a Lead interface for type safety when accessing lead properties
interface Lead {
  id?: string;
  assignedTo?: string;
  status?: string;
  lastContactDate?: Date | string | Timestamp | null;
  createdAt?: Date | string | Timestamp | null;
  tenantId?: string;
  [key: string]: any; // Allow additional properties
}

// Get all challenges
export async function getChallenges(tenantId: string = LEGACY_TENANT_ID): Promise<Challenge[]> {
  try {
    const challengesRef = collection(db, "challenges");
    let q;

    if (tenantId === LEGACY_TENANT_ID) {
      q = query(challengesRef, orderBy("createdAt", "desc"));
    } else {
      q = query(challengesRef, where("tenantId", "==", tenantId), orderBy("createdAt", "desc"));
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data() as any;
      return {
        id: doc.id,
        ...data,
        startDate: data.startDate?.toDate?.() || data.startDate,
        endDate: data.endDate?.toDate?.() || data.endDate,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        tenantId: data.tenantId || LEGACY_TENANT_ID
      } as Challenge;
    }).filter(challenge => {
      if (tenantId === LEGACY_TENANT_ID) {
        return !challenge.tenantId || challenge.tenantId === LEGACY_TENANT_ID;
      }
      return challenge.tenantId === tenantId;
    });
  } catch (error) {
    logger.error("Error fetching challenges:", error);
    return [];
  }
}

// Create a new challenge
export async function createChallenge(challenge: Omit<Challenge, "id" | "createdAt">): Promise<string | null> {
  try {
    const challengeData = {
      ...challenge,
      createdAt: new Date(),
      participants: challenge.participants || [], // Ensure participants is always an array
      tenantId: challenge.tenantId || LEGACY_TENANT_ID
    };

    const docRef = await addDoc(collection(db, "challenges"), challengeData);
    return docRef.id;
  } catch (error) {
    logger.error("Error creating challenge:", error);
    return null;
  }
}

// Helper function to safely convert Firestore Timestamp to Date
const safeTimestampToDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;

  try {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    } else if (timestamp instanceof Date) {
      return timestamp;
    } else if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    }
  } catch (error) {
    logger.error("Error converting timestamp:", error);
  }

  return null;
};

// Helper function to safely format date for logging
const safeFormatDate = (date: any): string => {
  if (!date) return 'null';

  try {
    if (date instanceof Date) {
      return !isNaN(date.getTime()) ? date.toISOString() : 'Invalid Date';
    }
    return String(date);
  } catch (error) {
    return 'Error formatting date';
  }
};

// Helper function to ensure a valid Date object
const ensureValidDate = (dateInput: any): Date | null => {
  if (!dateInput) return null;

  // If it's already a Date object
  if (dateInput instanceof Date) {
    return isNaN(dateInput.getTime()) ? null : dateInput;
  }

  // If it's a Firestore Timestamp
  if (dateInput instanceof Timestamp) {
    try {
      return dateInput.toDate();
    } catch (error) {
      logger.error("Error converting Timestamp to Date:", error);
      return null;
    }
  }

  // If it's a string or something else, try to convert it
  try {
    const date = new Date(String(dateInput));
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    logger.error("Error creating Date from input:", error, dateInput);
    return null;
  }
};

// New helper function to parse any date format
function parseAnyDateFormat(dateInput: any): Date | null {
  if (!dateInput) return null;

  try {
    // Handle Firebase Timestamp
    if (typeof dateInput === 'object' && dateInput.seconds !== undefined) {
      return new Date(dateInput.seconds * 1000);
    }

    // Handle __time__ format (from your data)
    if (typeof dateInput === 'object' && dateInput.__time__) {
      const parsedDate = new Date(dateInput.__time__);
      logger.log(`Parsed __time__ format: ${dateInput.__time__} to ${parsedDate.toISOString()}`);
      return parsedDate;
    }

    // Handle string ISO format
    if (typeof dateInput === 'string') {
      const parsedDate = new Date(dateInput);
      if (!isNaN(parsedDate.getTime())) {
        logger.log(`Parsed string format: ${dateInput} to ${parsedDate.toISOString()}`);
        return parsedDate;
      }
    }

    // Handle Date object
    if (dateInput instanceof Date) {
      if (!isNaN(dateInput.getTime())) {
        return dateInput;
      }
      return null;
    }

    // Handle numeric timestamp
    if (typeof dateInput === 'number') {
      return new Date(dateInput);
    }

    // Handle history entry date format
    if (typeof dateInput === 'object') {
      const jsonString = JSON.stringify(dateInput);
      logger.log(`Attempting to parse complex object: ${jsonString}`);

      // Try to extract any date-like string from the object
      const datePattern = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
      const match = jsonString.match(datePattern);
      if (match) {
        const parsedDate = new Date(match[0]);
        if (!isNaN(parsedDate.getTime())) {
          logger.log(`Extracted date string ${match[0]} from object, parsed to ${parsedDate.toISOString()}`);
          return parsedDate;
        }
      }
    }

    logger.error("Unhandled date format:", typeof dateInput, dateInput);
    return null;
  } catch (error) {
    logger.error("Error parsing date:", error, dateInput);
    return null;
  }
}

// Get leaderboard for a specific challenge
export async function getChallengeLeaderboard(challengeId: string, tenantId: string = LEGACY_TENANT_ID): Promise<LeaderboardEntry[]> {
  try {
    logger.log("Fetching leaderboard for challenge:", challengeId);

    // Fetch the challenge data to determine metrics type and participants
    const challengeRef = doc(db, "challenges", challengeId);
    const challengeSnapshot = await getDoc(challengeRef);

    if (!challengeSnapshot.exists()) {
      logger.error("Challenge not found");
      return [];
    }

    const challengeData = challengeSnapshot.data() as Challenge;
    logger.log("Raw challenge data:", challengeData);

    const metricType = challengeData.metricType;
    const participants = challengeData.participants || [];

    // Get users from firestore
    const usersRef = collection(db, "users");
    let usersQuery;
    if (tenantId === LEGACY_TENANT_ID) {
      usersQuery = query(usersRef);
    } else {
      usersQuery = query(usersRef, where("tenantId", "==", tenantId));
    }
    const usersSnapshot = await getDocs(usersQuery);

    // Create a map of user IDs to names, only including participants
    const userMap = new Map<string, string>();
    usersSnapshot.forEach(doc => {
      const userData = doc.data() as Record<string, any>;

      // Filter legacy if needed
      if (tenantId === LEGACY_TENANT_ID && userData.tenantId && userData.tenantId !== LEGACY_TENANT_ID) return;

      // Only include users who are participants in the challenge
      if (participants.length === 0 || participants.includes(doc.id)) {
        // Use the full name if available, or display name, or fallback to email
        if ('firstName' in userData && 'lastName' in userData && userData.firstName && userData.lastName) {
          userMap.set(doc.id, `${userData.firstName} ${userData.lastName}`);
        } else if ('name' in userData && userData.name) {
          userMap.set(doc.id, userData.name);
        } else if ('displayName' in userData && userData.displayName) {
          userMap.set(doc.id, userData.displayName);
        } else if ('email' in userData && userData.email) {
          userMap.set(doc.id, userData.email);
        } else {
          userMap.set(doc.id, `User ${doc.id.substring(0, 5)}`);
        }
      }
    });

    logger.log("Found participants:", Array.from(userMap.entries()));

    // For this demo, we'll use the leads collection
    const leadsRef = collection(db, "leads");

    // Ensure we have valid date objects for the query
    const startDate = ensureValidDate(challengeData.startDate);
    const endDate = ensureValidDate(challengeData.endDate);

    logger.log("Processing challenge dates:", {
      rawStartDate: challengeData.startDate,
      rawEndDate: challengeData.endDate,
      parsedStartDate: startDate ? startDate.toISOString() : 'invalid',
      parsedEndDate: endDate ? endDate.toISOString() : 'invalid'
    });

    // If we don't have valid dates, return an empty result
    if (!startDate || !endDate) {
      logger.error("Invalid challenge dates detected, falling back to mock data");
      return generateLeaderboardData(metricType, participants);
    }

    // FIRESTORE QUERY APPROACH NEEDS TO BE MODIFIED
    // Since Firestore queries are strict with data types and formats, we'll fetch all leads
    // and filter them in memory for more flexibility with date formats
    let leads: Lead[] = [];

    try {
      // First fetch all leads with status "won" for leads_closed
      // or all leads for other metrics
      let baseQuery;

      if (metricType === "leads_closed") {
        logger.log("Fetching all 'won' leads for manual date filtering");
        if (tenantId === LEGACY_TENANT_ID) {
          baseQuery = query(leadsRef, where("status", "==", "won"));
        } else {
          baseQuery = query(leadsRef, where("status", "==", "won"), where("tenantId", "==", tenantId));
        }
      } else {
        logger.log("Fetching all leads for manual date filtering");
        if (tenantId === LEGACY_TENANT_ID) {
          baseQuery = query(leadsRef);
        } else {
          baseQuery = query(leadsRef, where("tenantId", "==", tenantId));
        }
      }

      const querySnapshot = await getDocs(baseQuery);

      // Fix: Create Lead objects properly with explicit typing and type casting
      leads = querySnapshot.docs.map(doc => {
        // Cast Firestore data to Record<string, any> for proper type safety
        const data = doc.data() as Record<string, any>;

        // Create a new lead object with typed properties
        const lead: Lead = {
          id: doc.id,
          tenantId: data.tenantId || LEGACY_TENANT_ID
        };

        // Safely add properties with explicit type checking
        if ('assignedTo' in data && data.assignedTo) {
          lead.assignedTo = data.assignedTo as string;
        }

        if ('status' in data && data.status) {
          lead.status = data.status as string;
        }

        if ('lastContactDate' in data) {
          lead.lastContactDate = data.lastContactDate;
        }

        if ('createdAt' in data) {
          lead.createdAt = data.createdAt;
        }

        // Add any other properties that might exist
        Object.keys(data).forEach(key => {
          if (!lead.hasOwnProperty(key)) {
            lead[key] = data[key];
          }
        });

        return lead;
      }).filter(lead => {
        if (tenantId === LEGACY_TENANT_ID) {
          return !lead.tenantId || lead.tenantId === LEGACY_TENANT_ID;
        }
        return lead.tenantId === tenantId;
      });

      logger.log(`Found ${leads.length} total leads before date filtering`);
    } catch (error) {
      logger.error("Error fetching leads from Firestore:", error);
      return generateLeaderboardData(metricType, participants);
    }

    // Now manually filter the leads based on dates and metric type
    let filteredLeads = leads.filter(lead => {
      // Get the date we need to check based on metric type
      let dateToCheck;

      if (metricType === "leads_closed" || metricType === "leads_contacted") {
        dateToCheck = lead.lastContactDate;

        // For leads_closed, if no lastContactDate is present, try to find when the lead was marked as "won"
        if (!dateToCheck && metricType === "leads_closed" && lead.history && Array.isArray(lead.history) && lead.history.length > 0) {
          // Find the history entry where the status changed to "won"
          const wonStatusEntry = lead.history.find((entry: any) =>
            entry.type === 'updated' &&
            entry.changes &&
            entry.changes.some((change: any) =>
              change.field === 'status' &&
              change.newValue === 'won'
            )
          );

          if (wonStatusEntry && wonStatusEntry.date) {
            dateToCheck = wonStatusEntry.date;
            logger.log(`Using status change to won date for lead ${lead.id}:`, dateToCheck);
          }
        }

        // If no lastContactDate field is present for leads_contacted, try to get latest contact from history
        if (!dateToCheck && metricType === "leads_contacted" && lead.history && Array.isArray(lead.history) && lead.history.length > 0) {
          // Sort history by date descending to get the latest entry
          const sortedHistory = [...lead.history].sort((a: any, b: any) => {
            const dateA = parseAnyDateFormat(a.date);
            const dateB = parseAnyDateFormat(b.date);
            if (dateA && dateB) {
              return dateB.getTime() - dateA.getTime();
            }
            return 0;
          });

          // Use the date of the most recent history entry
          if (sortedHistory[0] && sortedHistory[0].date) {
            dateToCheck = sortedHistory[0].date;
            logger.log(`Using latest history date for lead ${lead.id}:`, dateToCheck);
          }
        }
      } else { // new_leads
        // Check for createdAt field first
        dateToCheck = lead.createdAt;

        // If no createdAt field is present, try to get creation date from history
        if (!dateToCheck && lead.history && Array.isArray(lead.history) && lead.history.length > 0) {
          // Look for the 'created' type entry in history
          const createdHistoryEntry = lead.history.find((entry: any) => entry.type === 'created');
          if (createdHistoryEntry && createdHistoryEntry.date) {
            dateToCheck = createdHistoryEntry.date;
            logger.log(`Using history created date for lead ${lead.id}:`, dateToCheck);
          }
        }
      }

      // Parse the date (handle various formats)
      const parsedDate = parseAnyDateFormat(dateToCheck);

      if (!parsedDate) {
        logger.log(`Lead ${lead.id} has invalid or missing date:`, dateToCheck);
        return false;
      }

      // Check if the date is within the challenge period
      const isWithinPeriod = parsedDate >= startDate && parsedDate <= endDate;

      if (isWithinPeriod) {
        logger.log(`Lead ${lead.id} is within the challenge period:`, {
          dateToCheck: dateToCheck,
          parsedDate: parsedDate.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      }

      return isWithinPeriod;
    });

    logger.log(`Found ${filteredLeads.length} leads after date filtering`);

    // Count leads by assigned user, only for participants
    const userScores = new Map<string, number>();

    filteredLeads.forEach(lead => {
      const userId = lead.assignedTo;

      if (userId && (participants.length === 0 || participants.includes(userId))) {
        userScores.set(userId, (userScores.get(userId) || 0) + 1);
      }
    });

    logger.log("User scores from filtered leads:", Array.from(userScores.entries()));

    // Convert to LeaderboardEntry array and sort by score
    const entries: LeaderboardEntry[] = Array.from(userScores.entries()).map(([userId, score]) => ({
      userId,
      userName: userMap.get(userId) || `User ${userId.substring(0, 5)}`,
      score
    }));

    // If we have entries from the database, use them instead of mock data
    if (entries.length > 0) {
      logger.log("Using real data for leaderboard");

      // Sort by score and add rank
      return entries
        .sort((a, b) => b.score - a.score)
        .map((entry, index) => ({
          ...entry,
          rank: index + 1
        }));
    }

    logger.log("No real data found, falling back to mock data");
    // If we have no entries from the database, generate mock data
    return generateLeaderboardData(metricType, participants);

  } catch (error) {
    logger.error("Error fetching challenge leaderboard:", error);
    // Fallback to mock data if something goes wrong
    return generateLeaderboardData();
  }
}

// Helper function to generate mock leaderboard data based on metric type
function generateLeaderboardData(metricType: string = "leads_closed", participants: string[] = []): LeaderboardEntry[] {
  const mockNames = [
    "Max Mustermann", "Laura Schmidt", "Thomas Weber", "Anna Becker",
    "Michael Fischer", "Sophie Wagner", "Daniel Hoffmann", "Emma Koch",
    "Felix Meyer", "Lisa Schulz", "Martin Bauer", "Julia Hoffmann"
  ];

  // If we have participants, only generate data for them
  const namesToUse = participants.length > 0
    ? mockNames.slice(0, participants.length)
    : mockNames.slice(0, 7);

  // Generate different score ranges based on metric type
  let maxScore: number;

  switch (metricType) {
    case "leads_closed":
      maxScore = 40;
      break;
    case "leads_contacted":
      maxScore = 100;
      break;
    case "new_leads":
      maxScore = 60;
      break;
    default:
      maxScore = 50;
  }

  // Create leaderboard entries with random scores
  const entries: LeaderboardEntry[] = namesToUse.map((name, index) => {
    let baseScore = maxScore - (index * (maxScore / 10));
    // Add some randomness
    const randomFactor = Math.floor(Math.random() * 5) - 2; // -2 to +2
    baseScore = Math.max(1, Math.floor(baseScore + randomFactor));

    return {
      userId: participants[index] || `user-${index + 1}`,
      userName: name,
      score: baseScore,
      rank: index + 1
    };
  });

  return entries;
}

// Update an existing challenge
export async function updateChallenge(challengeId: string, challenge: Partial<Challenge>): Promise<boolean> {
  try {
    // Convert Date objects to Timestamps for Firestore
    const dataToUpdate: Record<string, any> = { ...challenge };
    if (challenge.startDate && challenge.startDate instanceof Date) {
      dataToUpdate.startDate = Timestamp.fromDate(challenge.startDate);
    }
    if (challenge.endDate && challenge.endDate instanceof Date) {
      dataToUpdate.endDate = Timestamp.fromDate(challenge.endDate);
    }

    const challengeRef = doc(db, "challenges", challengeId);
    await updateDoc(challengeRef, dataToUpdate);
    return true;
  } catch (error) {
    logger.error("Error updating challenge:", error);
    return false;
  }
}

// Delete a challenge
export async function deleteChallenge(challengeId: string): Promise<boolean> {
  try {
    const challengeRef = doc(db, "challenges", challengeId);
    await deleteDoc(challengeRef);
    return true;
  } catch (error) {
    logger.error("Error deleting challenge:", error);
    return false;
  }
}
